import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAxiosInstance } from '@gov-nx/api/axios';
import { usePoQuery } from '@gov-nx/api/hooks/usePoQuery';
import { fetchDataBoxesQuery } from '@gov-nx/api/queries/dataBoxQuery';
import type { DatovkyApiSeznam } from '@gov-nx/api/types/databoxTypes';
import { useAppContext } from '@gov-nx/core/app/context/AppContext';
import { useExtraConfigContext } from '@gov-nx/core/app/extra-config/ExtraConfigProvider';
import { pipe } from '@gov-nx/core/types/pipe';
import { useDataBoxStore } from '@gov-nx/store/data-box/store';
import { sortDataboxesBeforeStoringToState } from '@gov-nx/utils/common/Databoxes/databox-sorting-utils';
import { transformDataBoxes } from '@gov-nx/utils/common/Databoxes/transformDataboxes';

const queryKey = ['data-box-list'];

export const useDataBoxLoader = () => {
	const { setAppError } = useAppContext();
	const { extraConfig } = useExtraConfigContext();
	const setDataBoxList = useDataBoxStore('setDataBoxList');
	const databoxTransformConfig =
		extraConfig.pageRoutes.databoxConfig.databoxTransformConfig;

	const onSuccess = (data: DatovkyApiSeznam) => {
		if (!data || !data.seznam || !Array.isArray(data.seznam)) {
			setAppError(new Error('DataBoxLoader: Invalid data received from API'));
			return;
		}

		setDataBoxList(
			data.seznam.length
				? pipe(
						data.seznam,
						transformDataBoxes(databoxTransformConfig),
						sortDataboxesBeforeStoringToState
					)
				: []
		);
	};

	const queryClient = useQueryClient();
	const { axiosInstance } = useAxiosInstance();
	const query = usePoQuery({
		queryKey,
		queryFn: () => fetchDataBoxesQuery(axiosInstance),
		onSuccess,
		onError: setAppError,
		enabled: false,
	});

	const initDataBoxes = useCallback(async (): Promise<void> => {
		await query.refetch();
	}, [query]);

	const refetchDataBoxes = useCallback(async (): Promise<void> => {
		await queryClient.invalidateQueries({ queryKey });

		await query.refetch();
	}, [query, queryClient]);

	return {
		isFetched: query.isFetched,
		initDataBoxes,
		refetchDataBoxes,
		data: query.data,
	};
};
